/* eslint-disable */
// ============================================================
//  THEME — Helpers de design partagés pour le thème nocturne
//  Cohérence avec la landing v1 (gradient nuit + étincelles +
//  glassmorphism + boutons gradient rouge + shine).
//  Importé par Dashboard, AdminPanel, écrans secondaires.
//
//  THÈMES :
//  - "night" (par défaut) — gradient violet/bleu profond → noir
//  - "hc-light" (label "Feu" 🔥) — dégradé rouge HC ardent → noir
//                           profond, cards / contenu glassmorphism
//                           dark cohérent. L'id "hc-light" reste
//                           pour compat localStorage utilisateur ;
//                           le label visible est "Feu".
//
//  Le background est injecté via la CSS variable --app-bg pour
//  permettre un switch sans re-render React. NIGHT_BG renvoie
//  désormais cette variable (un seul export, transparent).
//
//  Pour le thème hc-original, applyTheme() set aussi un attribut
//  data-theme="hc-original" sur <html> — ce qui active un bloc
//  CSS conditionnel défini dans le composant <G/> de src/ui.jsx
//  qui force via !important les overrides nécessaires sur les
//  containers racines des écrans dark (texte foncé, fond sable),
//  sans modifier aucun fichier d'écran. Approche minimal-impact :
//  les CTAs rouges et heros sombres internes conservent leur
//  rendu hardcodé, ce qui les garde lisibles.
// ============================================================

import { useMemo } from "react";
import { motion } from "framer-motion";

// === Thèmes disponibles ===
// Chaque thème définit :
//   - bg       : gradient CSS du fond (injecté dans --app-bg)
//   - cta      : background du bouton CTA primaire (injecté dans --app-cta-bg)
//   - ctaText  : couleur du texte CTA (injecté dans --app-cta-text)
//   - ctaGlow  : couleur du box-shadow glow (injecté dans --app-cta-glow)
//   - ctaShine : couleur du shine sweep animé (injecté dans --app-cta-shine)
//   - accent   : couleur des halos décoratifs secondaires (injecté dans --app-accent)
export const THEMES = {
  night: {
    id: "night",
    label: "Nocturne",
    emoji: "🌌",
    bg:
      "radial-gradient(ellipse 80% 60% at 50% -10%, rgba(255,180,120,.08) 0%, transparent 50%), " +
      "radial-gradient(ellipse at top, #3B1F6E 0%, #1F1A4A 28%, #14132E 58%, #08081A 100%)",
    cta: "linear-gradient(135deg, #C8001A 0%, #FF1744 100%)",
    ctaText: "#fff",
    ctaGlow: "rgba(200,0,26,.45)",
    ctaShine: "rgba(255,255,255,.32)",
    accent: "rgba(124,58,237,.3)",
    // Vars de contenu (cards, tabs, footer d'écran de détail) :
    // glassmorphism dark cohérent avec le reste du thème nocturne.
    contentCardBg:     "rgba(255,255,255,.06)",
    contentCardBorder: "rgba(255,255,255,.1)",
    contentText:       "#fff",
    contentMuted:      "rgba(255,255,255,.65)",
    // Cartes de liste (annonces accueil + HorseRoad) : glass un peu plus clair
    // que le contenu détail, pour mieux ressortir sur le fond nocturne.
    listCardBg:          "linear-gradient(135deg, rgba(255,255,255,.26) 0%, rgba(255,255,255,.15) 100%)",
    listCardBgHover:     "linear-gradient(135deg, rgba(255,255,255,.32) 0%, rgba(255,255,255,.19) 100%)",
    listCardBorder:      "rgba(255,255,255,.34)",
    listCardBorderHover: "rgba(255,255,255,.44)",
    // Rôles (New Modern App) — système de tokens partagé par les 2 thèmes.
    // En Nocturne, l'accent rouge est ECLAIRCI (#FF4D5E) pour ne pas brûler sur le violet.
    role_bg:"var(--app-bg)", role_surface:"rgba(255,255,255,.06)", role_surface2:"rgba(255,255,255,.10)",
    role_text:"#F4F1FA", role_textMuted:"rgba(244,241,250,.68)", role_textFaint:"rgba(244,241,250,.44)",
    role_border:"rgba(255,255,255,.10)", role_accent:"#FF4D5E", role_accentSoft:"rgba(255,77,94,.16)",
    role_onAccent:"#FFFFFF", role_danger:"#FF6369",
    // Modales : fond OPAQUE obligatoire. --hc-surface vaut rgba(255,255,255,.06)
    // en Nocturne : utilise dans une modale, on voit la page au travers.
    role_overlay:"#1B1430", role_overlay2:"#241B3D",
  },
  // === Clair (New Modern App) — blanc cassé + rouge de marque, remplace Feu ===
  light: {
    id:"light", label:"Clair", emoji:"☀️",
    bg:"radial-gradient(ellipse 85% 50% at 50% -10%, rgba(200,0,26,.06) 0%, transparent 55%), linear-gradient(180deg, #FAFAF9 0%, #F5F4F2 100%)",
    cta:"linear-gradient(135deg, #C8001A 0%, #FF1744 100%)", ctaText:"#fff",
    ctaGlow:"rgba(200,0,26,.35)", ctaShine:"rgba(255,255,255,.5)", accent:"rgba(200,0,26,.10)",
    contentCardBg:"#FFFFFF", contentCardBorder:"#ECEAE8", contentText:"#17120F", contentMuted:"#6B6560",
    listCardBg:"#FFFFFF", listCardBgHover:"#FAFAF9", listCardBorder:"#ECEAE8", listCardBorderHover:"#E0DDDA",
    role_bg:"var(--app-bg)", role_surface:"#FFFFFF", role_surface2:"#F5F4F2",
    role_text:"#17120F", role_textMuted:"#6B6560", role_textFaint:"#9A938D",
    role_border:"#ECEAE8", role_accent:"#C8001A", role_accentSoft:"#FFF1F0",
    role_onAccent:"#FFFFFF", role_danger:"#E5484D",
    role_overlay:"#FFFFFF", role_overlay2:"#F5F4F2",
  },
  "hc-light": {
    // === Feu (id technique : "hc-light") ===
    // Fond global en dégradé du ROUGE HC ardent vers le NOIR profond,
    // évoque la chaleur d'un feu. Le pattern visuel : le gradient
    // rouge→noir entoure tous les écrans (NIGHT_BG = var(--app-bg)),
    // les cards/contenu adoptent le glassmorphism dark via les vars
    // --app-content-* (voir AdDetailScreen).
    //
    // Le bloc CSS conditionnel dans <G/> est volontairement réduit
    // au minimum : on garde juste les sparkles désactivées (elles
    // seraient redondantes avec le gradient rouge marqué).
    //
    // NB : l'id "hc-light" est conservé pour ne pas casser les
    // localStorage des utilisateurs qui ont déjà fait un choix de
    // thème — le label affiché est "Feu".
    id: "hc-light",
    label: "Feu",
    emoji: "🔥",
    bg:
      "radial-gradient(ellipse 85% 50% at 50% -10%, rgba(255,40,60,.35) 0%, transparent 55%), " +
      "radial-gradient(ellipse at top, #C8001A 0%, #7A0014 18%, #3C0008 42%, #160003 70%, #050001 100%)",
    cta:      "linear-gradient(135deg, #C8001A 0%, #FF1744 100%)",
    ctaText:  "#fff",
    ctaGlow:  "rgba(255,40,60,.55)",
    ctaShine: "rgba(255,255,255,.32)",
    accent:   "rgba(255,90,70,.4)",
    // Vars de contenu : cards blanches opaques (l'identité du
    // thème clair). Le contraste fort sur fond rouge/noir rend
    // les cards "îlots clairs" comme dans le screenshot Equisync.
    contentCardBg:     "#FFFFFF",
    contentCardBorder: "#E0DBD4",
    contentText:       "#1A1A2E",
    contentMuted:      "#7A7A8C",
    // Cartes de liste : tons "braises" chaleureux (jaune-orangé → orange → rouge),
    // chauds mais pas trop clairs, cohérents avec le thème Feu.
    listCardBg:          "linear-gradient(135deg, rgba(255,186,82,.30) 0%, rgba(255,114,54,.23) 55%, rgba(216,44,28,.19) 100%)",
    listCardBgHover:     "linear-gradient(135deg, rgba(255,200,104,.38) 0%, rgba(255,132,66,.29) 55%, rgba(230,56,34,.24) 100%)",
    listCardBorder:      "rgba(255,162,86,.48)",
    listCardBorderHover: "rgba(255,184,106,.62)",
  },
};

// CSS variable utilisée par tous les écrans. Sa valeur est injectée
// par applyTheme() au root du document. Default = night.
export const NIGHT_BG = "var(--app-bg)";

// === Appliquer un thème à l'app ===
// Set la CSS variable --app-bg au root + persiste en localStorage.
// Aucun re-render React nécessaire — tous les écrans qui utilisent
// NIGHT_BG (= var(--app-bg)) se mettent à jour automatiquement.
// Le 2ème paramètre `persist` permet d'appliquer un thème
// temporairement (sans toucher au localStorage) — utilisé pour
// les écrans landing/auth qui restent toujours en Nocturne
// quelle que soit la préférence utilisateur.
export function applyTheme(themeId, persist = true) {
  const theme = THEMES[themeId] || THEMES.night;
  if (typeof document !== "undefined") {
    const root = document.documentElement;
    const r = root.style;
    r.setProperty("--app-bg",        theme.bg);
    r.setProperty("--app-cta-bg",    theme.cta);
    r.setProperty("--app-cta-text",  theme.ctaText);
    r.setProperty("--app-cta-glow",  theme.ctaGlow);
    r.setProperty("--app-cta-shine", theme.ctaShine);
    r.setProperty("--app-accent",    theme.accent);
    // Vars de contenu pour les écrans de détail (cards, tabs, footer)
    // qui doivent suivre le thème actif (dark glass en nocturne,
    // blanc opaque en hc-light).
    r.setProperty("--app-content-card-bg",     theme.contentCardBg);
    r.setProperty("--app-content-card-border", theme.contentCardBorder);
    r.setProperty("--app-content-text",        theme.contentText);
    r.setProperty("--app-content-muted",       theme.contentMuted);
    // Cartes de liste (annonces accueil + HorseRoad)
    r.setProperty("--app-list-card-bg",           theme.listCardBg || theme.contentCardBg);
    r.setProperty("--app-list-card-bg-hover",     theme.listCardBgHover || theme.listCardBg || theme.contentCardBg);
    r.setProperty("--app-list-card-border",       theme.listCardBorder || theme.contentCardBorder);
    r.setProperty("--app-list-card-border-hover", theme.listCardBorderHover || theme.listCardBorder || theme.contentCardBorder);
    // Tokens par rôles (New Modern App) — clair + sombre partageront ces noms.
    r.setProperty("--hc-bg",          theme.role_bg || theme.bg);
    r.setProperty("--hc-surface",     theme.role_surface || theme.contentCardBg);
    r.setProperty("--hc-surface-2",   theme.role_surface2 || theme.contentCardBg);
    r.setProperty("--hc-overlay",     theme.role_overlay  || theme.contentCardBg);
    r.setProperty("--hc-overlay-2",   theme.role_overlay2 || theme.contentCardBg);
    r.setProperty("--hc-text",        theme.role_text || theme.contentText);
    r.setProperty("--hc-text-muted",  theme.role_textMuted || theme.contentMuted);
    r.setProperty("--hc-text-faint",  theme.role_textFaint || theme.contentMuted);
    r.setProperty("--hc-border",      theme.role_border || theme.contentCardBorder);
    r.setProperty("--hc-accent",      theme.role_accent || "#C8001A");
    r.setProperty("--hc-accent-soft", theme.role_accentSoft || "rgba(200,0,26,.12)");
    r.setProperty("--hc-on-accent",   theme.role_onAccent || "#fff");
    r.setProperty("--hc-danger",      theme.role_danger || "#E5484D");
    root.setAttribute("data-theme", theme.id);
  }
  if (persist) {
    try { localStorage.setItem("hc_theme", theme.id); } catch (e) {}
  }
  return theme.id;
}

// === Récupérer le thème actuel (depuis localStorage, défaut night) ===
export function getCurrentTheme() {
  try {
    const saved = localStorage.getItem("hc_theme");
    if (saved === "hc-light") return "night"; // Feu retiré (New Modern App) -> repli Nocturne
    if (saved && THEMES[saved]) return saved;
  } catch (e) {}
  return "night";
}

// === Couleurs sémantiques (à privilégier pour la cohérence) ===
// Ces couleurs restent identiques quel que soit le thème — c'est l'identité
// de la marque HorseConnex (le rouge HC notamment).
export const C = {
  red: "#C8001A",
  redLight: "#FF1744",
  redAccent: "#FF5A6E",
  green: "#22c55e",
  gold: "#FFD278",
  glassBg: "rgba(255,255,255,.05)",
  glassBgStrong: "rgba(255,255,255,.08)",
  glassBorder: "rgba(255,255,255,.1)",
  glassBorderStrong: "rgba(255,255,255,.18)",
  textOnDark: "#fff",
  textOnDarkMuted: "rgba(255,255,255,.65)",
  textOnDarkSubtle: "rgba(255,255,255,.45)",
};

// === Design tokens New Modern App (constantes JS, identiques dans les 2 thèmes) ===
export const RADIUS = { sm: 10, md: 16, lg: 24, pill: 999 };
export const SPACE  = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 };
export const TYPE   = { body: 16, label: 15, h3: 17, h2: 24, h1: 30 };

// === Étincelles de fond (réutilisable sur tous les écrans dark) ===
// `count` : nombre d'étoiles (défaut 24, plus discret que les 32 de la landing).
export function NightSparkles({ count = 24 }) {
  const stars = useMemo(() =>
    Array.from({ length: count }, (_, i) => {
      const palette = Math.random();
      const color = palette < 0.6
        ? { fill: "#fff", glow: "rgba(255,255,255,.95)" }
        : palette < 0.85
          ? { fill: C.redAccent, glow: "rgba(255,90,110,.95)" }
          : { fill: C.gold, glow: "rgba(255,210,120,.95)" };
      return {
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: 2.5 + Math.random() * 5,
        duration: 1.0 + Math.random() * 1.2,
        repeatDelay: 0.5 + Math.random() * 2.4,
        initialDelay: Math.random() * 2.0,
        fill: color.fill,
        glow: color.glow,
      };
    }), [count]);
  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
      {stars.map(s => (
        <motion.div
          key={s.id}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: [0, 1, 0], scale: [0, 1.3, 0] }}
          transition={{
            duration: s.duration,
            delay: s.initialDelay,
            repeat: Infinity,
            repeatDelay: s.repeatDelay,
            ease: "easeOut",
            times: [0, 0.45, 1],
          }}
          style={{
            position: "absolute",
            top: `${s.y}%`, left: `${s.x}%`,
            width: s.size, height: s.size,
            borderRadius: "50%",
            background: s.fill,
            boxShadow: `0 0 ${s.size * 3}px ${s.glow}, 0 0 ${s.size * 6}px ${s.glow}`,
          }}
        />
      ))}
    </div>
  );
}

// === Card glassmorphism dark — wrapper réutilisable ===
// `accent` : couleur du halo dans le coin (optionnel).
export function GlassCard({ children, accent, sx = {} }) {
  return (
    <div style={{
      background: C.glassBg,
      backdropFilter: "blur(12px)",
      border: `1px solid ${C.glassBorder}`,
      borderRadius: 16,
      padding: 16,
      position: "relative",
      overflow: "hidden",
      ...sx,
    }}>
      {accent && (
        <div style={{
          position: "absolute",
          top: -40, right: -40,
          width: 120, height: 120,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${accent} 0%, transparent 70%)`,
          filter: "blur(20px)",
          pointerEvents: "none",
        }} />
      )}
      <div style={{ position: "relative", zIndex: 1 }}>
        {children}
      </div>
    </div>
  );
}

// === Bouton primary avec shine effect (gradient rouge HC) ===
// Identique au CTA de la landing v1, réutilisable partout.
// Props :
//   - children   : contenu (label + emoji)
//   - onClick    : handler de clic
//   - disabled   : true → bouton désactivé (cursor not-allowed, opacity .55)
//   - loading    : true → bouton en cours, comme disabled MAIS cursor "wait"
//                  (utile pour les boutons d'auth pendant le submit async)
//   - sx         : overrides de style spécifiques au consommateur
export function PrimaryButton({ children, onClick, disabled = false, loading = false, sx = {} }) {
  const isOff = disabled || loading;
  return (
    <motion.button
      whileTap={{ scale: isOff ? 1 : 0.97 }}
      onClick={isOff ? undefined : onClick}
      disabled={isOff}
      style={{
        width: "100%",
        padding: "15px 24px",
        background: isOff
          ? "rgba(255,255,255,.12)"
          : "var(--app-cta-bg, linear-gradient(135deg, #C8001A 0%, #FF1744 100%))",
        color: isOff ? "#fff" : "var(--app-cta-text, #fff)",
        border: "none",
        borderRadius: 14,
        fontSize: 15, fontWeight: 800,
        fontFamily: "var(--fh)",
        letterSpacing: 0.6,
        cursor: loading ? "wait" : (disabled ? "not-allowed" : "pointer"),
        boxShadow: isOff
          ? "none"
          : "0 8px 22px var(--app-cta-glow, rgba(200,0,26,.45)), inset 0 1px 0 rgba(255,255,255,.18)",
        position: "relative", overflow: "hidden",
        opacity: isOff ? 0.55 : 1,
        ...sx,
      }}
    >
      {!isOff && (
        <motion.span
          aria-hidden
          initial={{ x: "-130%" }}
          animate={{ x: "230%" }}
          transition={{ duration: 1.2, repeat: Infinity, repeatDelay: 4, ease: "easeInOut" }}
          style={{
            position: "absolute", top: 0, left: 0, bottom: 0,
            width: "55%",
            background: "linear-gradient(110deg, transparent 0%, var(--app-cta-shine, rgba(255,255,255,.32)) 50%, transparent 100%)",
            pointerEvents: "none",
          }}
        />
      )}
      <span style={{ position: "relative", zIndex: 1 }}>{children}</span>
    </motion.button>
  );
}

// === Header dark glassmorphism (avec back optionnel + titre + droite) ===
export function DarkHeader({ title, onBack, right, sticky = true }) {
  return (
    <div style={{
      position: sticky ? "sticky" : "relative",
      top: 0, zIndex: 10,
      padding: "16px 18px 14px",
      background: "rgba(13,13,26,.55)",
      backdropFilter: "blur(14px)",
      borderBottom: `1px solid ${C.glassBorder}`,
      display: "flex", alignItems: "center", gap: 12,
    }}>
      {onBack && (
        <motion.button
          whileTap={{ scale: 0.92 }}
          onClick={onBack}
          style={{
            background: "rgba(255,255,255,.08)",
            border: `1px solid ${C.glassBorder}`,
            borderRadius: 10,
            width: 36, height: 36,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", flexShrink: 0,
            color: "#fff",
            fontSize: 18,
          }}
        >
          ←
        </motion.button>
      )}
      <div style={{
        fontFamily: "var(--fh)",
        fontSize: 18, fontWeight: 900,
        color: "#fff",
        letterSpacing: 0.3,
        flex: 1,
      }}>
        {title}
      </div>
      {right}
    </div>
  );
}

